import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';

import { MatCardModule } from '@angular/material/card';
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';
import { CurrentUser } from '../../../../common/models/user';
import { CustomizerSettingsService } from '../../../../customizer-settings/customizer-settings.service';
import { ApplicationService } from '../../../../common/services/application.service';
import { StorageService } from '../../../../common/services/storage.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Guid } from 'guid-typescript';
import { SubmitApplicationCompliance } from '../../../../common/models/application';
import { Subject, takeUntil } from 'rxjs';

export type TriState = true | false | 'NP';

@Component({
  selector: 'app-comply',
  templateUrl: './comply.component.html',
  styleUrls: ['./comply.component.scss'],
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatCardModule,
    MatRadioModule,
    MatButtonModule,
    ReactiveFormsModule,
    FormsModule
]
})
export class ComplyComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  complyForm: FormGroup;
  
  applicationId!: string;
  user: CurrentUser | null = null;
  isToggled = false;

  private readonly snackbarConfig: MatSnackBarConfig = {
    duration: 3000,
    horizontalPosition: 'right',
    verticalPosition: 'top'
  };

  fields = [
    { label: '1. Prijava je dostavljena na pravi javni poziv/natječaj', control: 'isDeliveredToCorrectCall' },
    { label: '2. Prijava je dostavljena u zadanome roku		', control: 'isDeliveredWithinDeadline' },
    { label: '3. Prijava je napisana na hrvatskom jeziku', control: 'isApplicationInCroatianLanguage' },
    { label: '4. Sva obvezna polja prijave u aplikaciji su ispunjena', control: 'areAllMandatoryFieldsFilled' },
    { label: '5. Prijavitelj (i partner, ako je primjenjivo) prihvatljivi su sukladno uvjetima propisanima Uputama za prijavitelje', control: 'areApplicantAndPartnerEligible' },
    { label: '6. Lokacija provedbe programa/projekta/manifestacije je prihvatljiva', control: 'isImplementationLocationAcceptable' },
    { label: '7. Zatraženi iznos sredstava je unutar financijskih pragova postavljenih u javnom pozivu/natječaju', control: 'isRequestedAmountWithinLimits' },
    { label: '8. Propisani i ovjereni obrazac Izjave o nepostojanju dvostrukog financiranja je učitan u aplikaciju', control: 'isNoDoubleFundingStatementSubmitted' },
    { label: '9. Potvrda nadležne Porezne uprave koja potvrđuje da korisnik (pravna osoba – prijavitelj) nema dug (ne starija od 30 dana od dana predaje potvrde) učitana u aplikaciju', control: 'isNoDebtCertificateSubmitted' },
    { label: '10. Uvjerenje nadležnog suda da se protiv osobe ovlaštene za zastupanje udruge i voditelja projekta/manifestacije ne vodi kazneni postupak (ne starije od 6 mjeseci od dana predaje uvjerenja) učitano u aplikaciju', control: 'isNoCriminalProceedingsCertificateSubmitted' },
    { label: '11. Obrazac Izjave o suglasnosti za uvid u kaznenu evidenciju za osobe koje provode aktivnosti s djecom (ako je primjenjivo) potpisan i učitan u aplikaciju', control: 'isCriminalRecordCheckConsentSubmitted' },
    { label: '12. Preslika važećeg Statuta uz dokaz (dopis) da je Zahtjev za usklađivanje statuta sa Zakonom o udrugama predan na ovjeru Uredu državne uprave (samo za udruge koje su podnijele zahtjev za usklađivanje statuta sa Zakonom  o udrugama, ali nadležni ured državne uprave još nije obradio i izdao rješenje)', control: 'isStatuteProofSubmitted' },
    { label: '13. Propisani obrazac Izjave o partnerstvu je potpisan, ovjeren i učitan u aplikaciju (samo za manifestacije)', control: 'isPartnershipStatementSubmitted' },
    { label: '14. Dokaz da je prijavitelj/partner upisan u Registar udruga odnosno drugi odgovarajući registar (preslika iz nadležnog registra) učitan u aplikaciju, ako je primjenjivo (samo za manifestacije)', control: 'isAssociationRegistryProofSubmitted' },
    { label: '15. Dokaz da je prijavitelj/partner upisan u Registar neprofitnih organizacija odnosno drugi odgovarajući registar (preslika iz Registra neprofitnih organizacija) učitan u aplikaciju, ako je primjenjivo (samo za manifestacije)		', control: 'isNonProfitRegistryProofSubmitted' },
    { label: '16. U aplikaciju učitana izjava da je prijavitelj/partner uredno ispunio obveze iz svih prethodno sklopljenih ugovora o financiranju iz proračuna Općine i drugih javnih izvora, nema dugovanja s osnove plaćanja doprinosa za mirovinsko i zdravstveno osiguranje i plaćanja poreza te drugih davanja prema državnom proračunu, proračunu Općine te trgovačkim društvima u vlasništvu Općine i da prijavitelj uredno predaje izvješća Općini (Obrazac izjave o ispunjenim obvezama) (samo za manifestacije)', control: 'isNoOutstandingObligationsStatementSubmitted' }
  ];

  constructor(
    public themeService: CustomizerSettingsService,
    private fb: FormBuilder,
    private applicationService: ApplicationService,
    private storageService: StorageService,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private router: Router
  ) {
    this.complyForm = this.fb.group({
      isDeliveredToCorrectCall: ['', Validators.required],
      isDeliveredWithinDeadline: ['', Validators.required],
      isApplicationInCroatianLanguage: ['', Validators.required],
      areAllMandatoryFieldsFilled: ['', Validators.required],
      areApplicantAndPartnerEligible: ['', Validators.required],
      isImplementationLocationAcceptable: ['', Validators.required],
      isRequestedAmountWithinLimits: ['', Validators.required],
      isNoDoubleFundingStatementSubmitted: ['', Validators.required],
      isNoDebtCertificateSubmitted: ['', Validators.required],
      isNoCriminalProceedingsCertificateSubmitted: ['', Validators.required],
      isCriminalRecordCheckConsentSubmitted: ['', Validators.required],
      isStatuteProofSubmitted: ['', Validators.required],
      isPartnershipStatementSubmitted: ['', Validators.required],
      isAssociationRegistryProofSubmitted: ['', Validators.required],
      isNonProfitRegistryProofSubmitted: ['', Validators.required],
      isNoOutstandingObligationsStatementSubmitted: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.themeService.isToggled$.pipe(takeUntil(this.destroy$)).subscribe(isToggled => {
      this.isToggled = isToggled;
    });

    this.storageService.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.user = user;
    });

    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.applicationId = params.get('applicationId') || '';
    });

    this.applicationService.getComplies(Guid.parse(this.applicationId)).pipe(takeUntil(this.destroy$)).subscribe({
      next: (compliances) => {
        // We pick the first or only compliance
        const existingCompliance = compliances.length > 0 ? compliances[0] : null;
        if (existingCompliance) {
          this.complyForm.patchValue({
            isDeliveredToCorrectCall: this.booleanToTriState(existingCompliance.isDeliveredToCorrectCall),
            isDeliveredWithinDeadline: this.booleanToTriState(existingCompliance.isDeliveredWithinDeadline),
            isApplicationInCroatianLanguage: this.booleanToTriState(existingCompliance.isApplicationInCroatianLanguage),
            areAllMandatoryFieldsFilled: this.booleanToTriState(existingCompliance.areAllMandatoryFieldsFilled),
            areApplicantAndPartnerEligible: this.booleanToTriState(existingCompliance.areApplicantAndPartnerEligible),
            isImplementationLocationAcceptable: this.booleanToTriState(existingCompliance.isImplementationLocationAcceptable),
            isRequestedAmountWithinLimits: this.booleanToTriState(existingCompliance.isRequestedAmountWithinLimits),
            isNoDoubleFundingStatementSubmitted: this.booleanToTriState(existingCompliance.isNoDoubleFundingStatementSubmitted),
            isNoDebtCertificateSubmitted: this.booleanToTriState(existingCompliance.isNoDebtCertificateSubmitted),
            isNoCriminalProceedingsCertificateSubmitted: this.booleanToTriState(existingCompliance.isNoCriminalProceedingsCertificateSubmitted),
            isCriminalRecordCheckConsentSubmitted: this.booleanToTriState(existingCompliance.isCriminalRecordCheckConsentSubmitted),
            isStatuteProofSubmitted: this.booleanToTriState(existingCompliance.isStatuteProofSubmitted),
            isPartnershipStatementSubmitted: this.booleanToTriState(existingCompliance.isPartnershipStatementSubmitted),
            isAssociationRegistryProofSubmitted: this.booleanToTriState(existingCompliance.isAssociationRegistryProofSubmitted),
            isNonProfitRegistryProofSubmitted: this.booleanToTriState(existingCompliance.isNonProfitRegistryProofSubmitted),
            isNoOutstandingObligationsStatementSubmitted: this.booleanToTriState(existingCompliance.isNoOutstandingObligationsStatementSubmitted)
          });
        }
      },
      error: (err) => {
        console.error('Error loading compliance list', err);
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }


  private booleanToTriState(value: boolean | null | undefined): TriState {
    if (value === true)  return true;
    if (value === false) return false;
    return 'NP'; // default if null/undefined => NP
  }

  private triStateToBoolean(value: TriState): boolean | null {
    if (value === true)  return true;
    if (value === false) return false;
    return null; // 'NP' => store as null in the backend
  }

  submit(): void {
    if (this.complyForm.invalid) {
      return;
    }

    const formValue = this.complyForm.value;

    const submitComply: SubmitApplicationCompliance = {
      applicationId: this.applicationId,
      evaluatorId: this.user!.id,
      isDeliveredToCorrectCall: this.triStateToBoolean(formValue.isDeliveredToCorrectCall),
      isDeliveredWithinDeadline: this.triStateToBoolean(formValue.isDeliveredWithinDeadline),
      isApplicationInCroatianLanguage: this.triStateToBoolean(formValue.isApplicationInCroatianLanguage),
      areAllMandatoryFieldsFilled: this.triStateToBoolean(formValue.areAllMandatoryFieldsFilled),
      areApplicantAndPartnerEligible: this.triStateToBoolean(formValue.areApplicantAndPartnerEligible),
      isImplementationLocationAcceptable: this.triStateToBoolean(formValue.isImplementationLocationAcceptable),
      isRequestedAmountWithinLimits: this.triStateToBoolean(formValue.isRequestedAmountWithinLimits),
      isNoDoubleFundingStatementSubmitted: this.triStateToBoolean(formValue.isNoDoubleFundingStatementSubmitted),
      isNoDebtCertificateSubmitted: this.triStateToBoolean(formValue.isNoDebtCertificateSubmitted),
      isNoCriminalProceedingsCertificateSubmitted: this.triStateToBoolean(formValue.isNoCriminalProceedingsCertificateSubmitted),
      isCriminalRecordCheckConsentSubmitted: this.triStateToBoolean(formValue.isCriminalRecordCheckConsentSubmitted),
      isStatuteProofSubmitted: this.triStateToBoolean(formValue.isStatuteProofSubmitted),
      isPartnershipStatementSubmitted: this.triStateToBoolean(formValue.isPartnershipStatementSubmitted),
      isAssociationRegistryProofSubmitted: this.triStateToBoolean(formValue.isAssociationRegistryProofSubmitted),
      isNonProfitRegistryProofSubmitted: this.triStateToBoolean(formValue.isNonProfitRegistryProofSubmitted),
      isNoOutstandingObligationsStatementSubmitted: this.triStateToBoolean(formValue.isNoOutstandingObligationsStatementSubmitted)
    };

    this.applicationService.comply(submitComply).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.snackBar.open('Obrazac ispunjenja formalnih uvjeta uspješno podnesen', 'Zatvori', this.snackbarConfig);
        this.router.navigate(['/municipality/applications']);
      },
      error: (error) => {
        this.snackBar.open('Greška prilikom podnošenja obrasca ispunjenja formalnih uvjeta', 'Zatvori', this.snackbarConfig);
        console.error(error);
      }
    });
  }
}